import React, { useState, useEffect, useMemo } from 'react';
import { Form, Button, Alert } from '@/components/ui';
import type { WorkIdDetail } from '../../../services/workIdService';
import { routineTaskService } from '../../../services/routineTaskService';
import ResponsiveModal from '../../../components/ResponsiveModal';

interface WorkIdCloseTaskModalProps {
  visible: boolean;
  onClose: () => void;
  workId: WorkIdDetail | null;
  onSuccess: () => void;
}

const WorkIdCloseTaskModal: React.FC<WorkIdCloseTaskModalProps> = ({
  visible,
  onClose,
  workId,
  onSuccess,
}) => {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  const routineActiveTask = useMemo(() => {
    const t = workId?.task;
    if (!t) return null;
    return t.type === 'Routine' && (t.status?.toUpperCase() ?? '') === 'ACTIVE' ? t : null;
  }, [workId?.task]);

  useEffect(() => {
    if (visible) {
      setReason('');
      setError(null);
      setValidationError(null);
    }
  }, [visible]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = reason.trim();
    if (!trimmed) {
      setValidationError('Reason is required.');
      return;
    }
    if (!routineActiveTask) return;
    setValidationError(null);
    setError(null);
    setSubmitting(true);
    try {
      await routineTaskService.updateTaskStatus(routineActiveTask.id, 'CLOSED', trimmed);
      onSuccess();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to close task');
    } finally {
      setSubmitting(false);
    }
  };

  if (!workId) return null;

  const title = `Close task - Work ID ${workId.workIdentificationId ?? workId.id}`;

  if (!routineActiveTask) {
    return (
      <ResponsiveModal
        isOpen={visible}
        onClose={onClose}
        title={title}
        size="medium"
        footer={
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        }
      >
        <p className="tw-text-content-muted tw-mb-0">
          No active Routine task is linked to this Work ID. There is nothing to close.
        </p>
      </ResponsiveModal>
    );
  }

  return (
    <ResponsiveModal
      isOpen={visible}
      onClose={onClose}
      title={title}
      size="medium"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Closing…' : 'Close task'}
          </Button>
        </>
      }
    >
      <Form onSubmit={handleSubmit}>
        {error && (
          <Alert variant="danger" className="tw-py-2">
            {error}
          </Alert>
        )}
        <p className="tw-text-content-muted tw-text-sm tw-mb-2">
          Task #{routineActiveTask.id} ({routineActiveTask.taskNumber ?? 'Routine'}) will be closed.
        </p>
        <Form.Group className="tw-mb-0">
          <Form.Label>Reason <span className="tw-text-danger">*</span></Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              setValidationError(null);
            }}
            isInvalid={!!validationError}
            placeholder="Enter reason for closing this task…"
          />
          {validationError && (
            <Form.Control.Feedback type="invalid">{validationError}</Form.Control.Feedback>
          )}
        </Form.Group>
      </Form>
    </ResponsiveModal>
  );
};

export default WorkIdCloseTaskModal;
